import { Fragment, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { Box, Column, Divider, Typography } from '@tempmee/react';
import { CenteredSpinner } from '~/commonComponents';
import {
  CandidateSortableField,
  ICandidateListViewEntity,
  useGetCandidateListInfinite,
} from '~/domain/PermanentJobs';
import { showErrorMessage } from '~/domain/utils';
import { useIsFavoriteProfessional } from '~/hooks/useIsFavoriteProfessional';
import type { ICandidateSection } from '../../postingDetailsRules';
import { CandidateRow } from './CandidateRow';

interface ICandidateSectionProps {
  jobPostingId: string;
  section: ICandidateSection;
  onOpenCandidate: (candidate: ICandidateListViewEntity) => void;
}

/**
 * One status group of the Candidates tab: its own paged list request, filtered to the
 * section's statuses and newest first, loading the next page as the office scrolls
 * past the last row. A section is its own component because each one needs its own
 * infinite query and its own scroll sentinel.
 */
export const CandidateSection = ({
  jobPostingId,
  section,
  onOpenCandidate,
}: ICandidateSectionProps) => {
  const { data, error, fetchNextPage, hasNextPage, isError, isFetchingNextPage, isLoading } =
    useGetCandidateListInfinite(
      {
        jobPostingId,
        status: section.statuses,
        orderBy: CandidateSortableField.CreatedAt,
        orderDirection: 'DESC',
      },
      { enabled: !!jobPostingId },
    );

  const { ref: loadMoreRef, inView } = useInView();
  const isFavoriteProfessional = useIsFavoriteProfessional();

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    if (isError) {
      showErrorMessage({ error });
    }
  }, [isError, error]);

  return (
    <Column
      gap={2}
      p={4}
      className="w-full rounded-lg bg-background"
      data-cy={`candidate-section-${section.key}`}
      data-testid={`candidate-section-${section.key}`}
    >
      <Typography variant="bodyLarge" as="h2" className="text-text-secondary">
        {section.title}
      </Typography>
      {isLoading ? (
        <CenteredSpinner />
      ) : (
        data.map((candidate, index) => (
          <Fragment key={candidate.id}>
            {index > 0 ? <Divider /> : null}
            <CandidateRow
              candidate={candidate}
              isFavorite={isFavoriteProfessional(candidate.professional.id)}
              onOpen={onOpenCandidate}
            />
          </Fragment>
        ))
      )}
      {hasNextPage ? (
        <Box ref={loadMoreRef} height="1px" data-testid="candidate-section-sentinel" />
      ) : null}
      {isFetchingNextPage ? <CenteredSpinner /> : null}
    </Column>
  );
};
